import { apiUrl, getAuthHeaders } from "./network_calls";
import { createReadCache, withReadAbort } from './readCache';
export const usageApiUrl = (path: string) => {
  const base = process.env.REACT_APP_USAGE_API_URL;
  return base ? `${base.replace(/\/$/, "")}${path}` : apiUrl(path);
};
export type UsageSource = "classic" | "gambit";
export type UsageEntry = Record<string, any> & { id: string };
export type UsagePage = {
  available?: boolean;
  reason?: string;
  coverage?: string;
  entries: UsageEntry[];
  summary?: {
    records: number;
    providerCost: number;
    modelTokens: number;
    imageTokens: number;
    unreported: number;
  };
  spend?: Array<{
    day: string;
    kind: string;
    tokens: number;
    unpriced: number;
    refunded?: number;
  }>;
  mode?: string;
  productionBilling?: boolean;
  categories?: Array<{
    category: string;
    records: number;
    providerCost: number;
    fableTokens: number;
    unpriced: number;
  }>;
  scopes?: Array<Record<string, string>>;
  nextCursor?: string;
  hasMore?: boolean;
  before?: number;
  tokensPerUsd?: number;
  conversionStatus?: string;
};
export async function loadUsage(
  source: UsageSource,
  filters: Record<string, string>,
  cursor?: string,
  signal?: AbortSignal,
): Promise<UsagePage> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, 15000);
  const bounded = controller.signal;
  try {
    const query = new URLSearchParams({ source });
    for (const key of ["from", "to"]) if (filters[key]) query.set(key, filters[key]);
    if (cursor) query.set("cursor", cursor);
    const headers = await getAuthHeaders();
    if (!headers.Authorization) throw new Error("Sign in to view usage.");
    const response = await fetch(`${usageApiUrl("/usage")}?${query}`, { headers, signal: bounded });
    if (!response.ok) throw new Error("Could not load usage. Please try again.");
    return response.json();
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}
export function mergeUsage(previous: UsageEntry[], next: UsageEntry[]) {
  const entries = new Map(previous.map((entry) => [entry.id, entry]));
  next.forEach((entry) => entries.set(entry.id, entry));
  return [...entries.values()].sort((a, b) =>
    String(b.createdAt || "").localeCompare(String(a.createdAt || "")),
  );
}
export function classicTotals(entries: UsageEntry[]) {
  return entries.reduce(
    (sum, entry) => {
      if (
        entry.status !== "APPLIED" ||
        typeof entry.tokenChange !== "number" ||
        !Number.isFinite(entry.tokenChange)
      )
        return sum;
      sum.added += entry.subscriptionTokensGranted || 0;
      if (["SYNC", "REVOKE", "SUBSCRIPTION"].includes(entry.operation))
        return sum;
      if (entry.tokenChange < 0) sum.spent -= entry.tokenChange;
      else if (/refund/i.test(entry.reason || entry.id))
        sum.refunded += entry.tokenChange;
      else sum.added += entry.tokenChange;
      return sum;
    },
    { spent: 0, refunded: 0, added: 0 },
  );
}

export type TokenBalance = {
  available: boolean;
  membership?: { tier: 'pro' | 'premium' | 'ultra' | null; webSubscription: boolean };
  trial?: { active: boolean; used: boolean; endsAt: string | null };
  purchased?: number;
  subscription?: number;
  total?: number;
  monthlyAllowance?: number;
  subscriptionExpiresAt?: string | null;
  subscriptionPeriodEndsAt?: string | null;
  subscriptionRenewsAt?: string | null;
  subscriptionWillRenew?: boolean | null;
  subscriptionStatus?:
    | "active"
    | "trialing"
    | "cancelled"
    | "expired"
    | "past_due"
    | null;
  nextSubscriptionAllocationAt?: string | null;
};
export const TOKEN_BALANCE_CACHE_MS = 60000;
const tokenBalanceCache = createReadCache<TokenBalance>(TOKEN_BALANCE_CACHE_MS);
export const clearTokenBalanceCache = tokenBalanceCache.clear;
export async function loadTokenBalance(
  signal?: AbortSignal,
  options: { force?: boolean; expectedTotal?: number | null } = {},
): Promise<TokenBalance> {
  const headers = await getAuthHeaders();
  if (signal?.aborted) throw new DOMException('The request was aborted.', 'AbortError');
  if (!headers.Authorization) throw new Error("Sign in to view token balances.");
  const endpoint = usageApiUrl('/usage/balance');
  // Authorization scopes this private cache to the actual signed-in session.
  const request = tokenBalanceCache.read(JSON.stringify([endpoint, headers.Authorization]), async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(endpoint, { headers, signal: controller.signal });
      if ([404, 501].includes(response.status)) return { available: false };
      if (!response.ok) throw new Error("Could not load token balances.");
      return await response.json();
    } finally { clearTimeout(timer); }
  }, { force: options.force, accept: value => options.expectedTotal == null || !value.available || value.total === options.expectedTotal });
  return withReadAbort(request, signal);
}
export const usageCategory = (kind: string) =>
  ({ model: "Chat", image: "Images", audio: "Audio", video: "Video" })[kind] ||
  "Chat";
